/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useMemo, useState } from 'react';
// Components
import {
  Button, InputsRadioGroup, MessageBanner, Skeleton, Switch, TextField,
} from '@repo/zolintra-ui';
import EmployeInformation from '@repo/zolintra-ui/components/atoms/EmployeInformation/EmployeInformation';
// Utils
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { cn } from '@repo/zolintra-utils';
import { isEmpty, isNil, sortBy } from 'lodash';
import { useMutation } from '@tanstack/react-query';
// Hooks
import useToast from '../../../../hooks/useToast';
import useResources from '../../../../hooks/domain/useResources';
import { useAppContext } from '../../../../store/AppContext';
import { timesOfTheDay, weekDays } from '../../../../hooks/useWeekDays';
// Permissions
import hasPermission, { PERMISSIONS } from '../../../../permissions/hasPermission';
// Services
import { useResourcesListQuery, useResourceQuery, useResourcesStaffListQuery } from '../../../../services/resource/resourceQueries';
import { resourceConfigurationFormSchema } from '../../../../services/types';
import { patchResourceConfiguration } from '../../../../services/resource/resourceService';
// Styles
import styles from './UnitResourceSettings.module.css';

const defaultValues = {
  maxRemoteWorkingPostsPerWeek: 2,
  isSubjectToRemoteWorkingRotationRule: false,
  canPostCompensatoryTimeLeave: false,
  hasPartTime: false,
  partTimeSchedule: {
    Monday: null,
    Tuesday: null,
    Wednesday: null,
    Thursday: null,
    Friday: null,
  },
};

function UnitResourceSettings() {
  // Hooks
  const { t } = useTranslation();
  const [{ user }] = useAppContext();
  const toast = useToast();
  const {
    getResourceDisplayName,
    getResourcePosition,
    getStaffResourcesListWithUserInfo,
  } = useResources();

  // Permissions
  const hasUpdatePermission = hasPermission(PERMISSIONS.UPDATE_ANY_RESOURCE, user.prm);

  // States
  const [selectedResource, setSelectedResource] = useState(null);
  const timesOfTheDayOptions = timesOfTheDay.map(
    (item) => ({ value: item, label: t(`settings.resource.timesOfTheDay.${item}`) }),
  );

  // Form
  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues,
    resolver: joiResolver(resourceConfigurationFormSchema),
    mode: 'onBlur',
    shouldFocusError: false,
  });
  const hasPartTime = watch('hasPartTime');
  const partTimeSchedule = watch('partTimeSchedule');

  // API Calls
  const resourcesListQuery = useResourcesListQuery();
  const resourcesStaffListQuery = useResourcesStaffListQuery();

  const resourcesList = useMemo(() => (
    sortBy(
      getStaffResourcesListWithUserInfo(
        resourcesStaffListQuery.data,
        resourcesListQuery.data,
      )
      || [],
      'firstName',
    )
  ), [resourcesListQuery?.data, resourcesStaffListQuery?.data]);

  const {
    data: resource,
    isLoading: isResourceLoading,
  } = useResourceQuery({
    resourceId: selectedResource?.id,
  }, {
    enabled: !isNil(selectedResource?.id),
    meta: {
      preventToastError: true,
    },
  });

  const { mutate: patchResourceMutate, isPending: isPatchResourcePending } = useMutation({
    // @ts-ignore - can't use typing on mutationFn
    mutationFn: (data) => patchResourceConfiguration(selectedResource.id, data),
    onSuccess: () => {
      toast.notify({
        type: 'success',
        message: t('settings.configurationUpdateSuccess'),
      });
    },
  });

  const serializeValuesForForm = (data) => {
    const values = {
      isSubjectToRemoteWorkingRotationRule: data.isSubjectToRemoteWorkingRotationRule || false,
      canPostCompensatoryTimeLeave: data.canPostCompensatoryTimeLeave || false,
      maxRemoteWorkingPostsPerWeek: data.maxRemoteWorkingPostsPerWeek || 2,
      hasPartTime: data.partTimeSchedule?.some((entry) => entry.duration !== 'None'),
      partTimeSchedule: {},
    };

    weekDays.forEach((day) => {
      const foundEntry = data.partTimeSchedule?.length
        ? data.partTimeSchedule.find((entry) => entry.day === day)
        : null;
      values.partTimeSchedule[day] = foundEntry ? foundEntry.duration : null;
    });

    return values;
  };

  useEffect(() => {
    reset(resource ? serializeValuesForForm(resource) : defaultValues);
  }, [resource]);

  const serializeValuesForApi = (values) => {
    let schedule = [];

    if (values.hasPartTime) {
      schedule = Object.keys(values.partTimeSchedule)
        .filter((day) => values.partTimeSchedule[day])
        .map((day) => ({ day, duration: values.partTimeSchedule[day] }));
    }

    return {
      version: resource?.version || 1,
      isSubjectToRemoteWorkingRotationRule: values.isSubjectToRemoteWorkingRotationRule,
      canPostCompensatoryTimeLeave: values.canPostCompensatoryTimeLeave,
      maxRemoteWorkingPostsPerWeek: +values.maxRemoteWorkingPostsPerWeek,
      partTimeSchedule: schedule,
    };
  };

  const onSubmit = (values) => {
    const valuesToSend = serializeValuesForApi(values);
    // @ts-ignore - can't use typing on mutationFn
    patchResourceMutate(valuesToSend);
  };

  const handleCancelSelectionIfSelected = ({ e, day }) => {
    if (e.target.value === partTimeSchedule[day]) {
      setValue('partTimeSchedule', { ...partTimeSchedule, [day]: 'None' });
    }
  };

  return (
    <div className={styles.grid}>
      <div className={styles.list}>
        {resourcesListQuery.isPending ? (
          Array.from({ length: 20 }).map((_, index) => (
            <Skeleton key={index} shape="rectangle" width="100%" height="40px" className="margin-bottom-xs" />
          ))
        ) : (
          resourcesList.map((res) => (
            <button
              type="button"
              key={res.id}
              onClick={() => setSelectedResource(res)}
              aria-label={getResourceDisplayName(res)}
              className={cn([
                styles.user,
                selectedResource?.id === res.id ? styles.selected : '',
              ])}
            >
              <EmployeInformation
                resource={{
                  name: getResourceDisplayName(res),
                  photo: res.photo,
                  position: getResourcePosition(res),
                }}
              />
            </button>
          ))
        )}
      </div>
      <div className={cn([styles.details, isResourceLoading ? styles['white-bg'] : ''])}>
        {isResourceLoading ? (
          <div>
            <Skeleton shape="rectangle" width="100%" height="85px" className="margin-bottom-s" />
            <Skeleton shape="rectangle" width="100%" height="54px" className="margin-bottom-s" />
            <Skeleton shape="rectangle" width="100%" height="54px" className="margin-bottom-l" />

            <Skeleton shape="rectangle" width="100%" height="85px" className="margin-bottom-s" />
            <Skeleton shape="rectangle" width="100%" height="54px" className="margin-bottom-s" />
            <Skeleton shape="rectangle" width="100%" height="54px" className="margin-bottom-s" />
          </div>
        ) : (
          selectedResource ? (
            <>
              <div className={styles.card}>
                <span className={styles['card-title']}>
                  {t('settings.resource.leaveAndWorkingTime')}
                </span>

                <div className={styles.table}>
                  <div className={styles.row}>
                    <span className={styles['field-label']}>
                      {t('settings.resource.rtt')}
                    </span>
                    <Switch
                      id="canPostCompensatoryTimeLeave"
                      {...register('canPostCompensatoryTimeLeave')}
                      disabled={!hasUpdatePermission}
                    />
                  </div>

                  <div className={styles['border-bottom']}>
                    <div className={cn([styles['flex-center-sb'], 'margin-top-s margin-bottom-s'])}>
                      <span className={styles['field-label']}>
                        {t('settings.resource.flexibleTime')}
                      </span>
                      <Switch
                        id="hasPartTime"
                        {...register('hasPartTime')}
                        disabled={!hasUpdatePermission}
                      />
                    </div>
                    {hasPartTime ? (
                      <div className={cn([styles.week, 'margin-bottom-s'])}>
                        {weekDays.map((day) => (
                          <div className={styles.day} key={day}>
                            <span className={styles['day-label']}>{t(`weekDays.${day}`)}</span>

                            <InputsRadioGroup
                              options={timesOfTheDayOptions}
                              onClick={(e) => handleCancelSelectionIfSelected({ e, day })}
                              // @ts-ignore
                              {...register(`partTimeSchedule.${day}`)}
                              disabled={!hasUpdatePermission}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                </div>
              </div>

              <div className={styles.card}>
                <span className={styles['card-title']}>
                  {t('settings.resource.remote')}
                </span>

                <div className={styles.table}>
                  <div className={styles.row}>
                    <span className={styles['field-label']}>
                      {t('settings.resource.maxPerWeek')}
                    </span>

                    <TextField
                      className={styles.field}
                      maxWidth={80}
                      type="number"
                      min={0}
                      max={5}
                      placeholder="2"
                      {...register('maxRemoteWorkingPostsPerWeek')}
                      disabled={!hasUpdatePermission}
                    />
                  </div>

                  <div className={styles.row}>
                    <span className={styles['field-label']}>
                      {t('settings.resource.rotation')}
                    </span>
                    <Switch
                      id="isSubjectToRemoteWorkingRotationRule"
                      {...register('isSubjectToRemoteWorkingRotationRule')}
                      disabled={!hasUpdatePermission}
                    />
                  </div>
                </div>
              </div>

              {!isEmpty(formErrors) ? (
                <div>
                  <MessageBanner color="error">
                    <div>
                      {Object.keys(formErrors).map((field) => (
                        <p key={field}>{t(formErrors[field].message)}</p>
                      ))}
                    </div>
                  </MessageBanner>
                </div>
              ) : null}

              {hasUpdatePermission ? (
                <section className={styles.actions}>
                  <Button
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isPatchResourcePending}
                  >
                    {t('settings.formAction')}
                  </Button>
                </section>
              ) : null}

            </>
          ) : null
        )}
      </div>
    </div>
  );
}

UnitResourceSettings.propTypes = {};

export default UnitResourceSettings;
